import { taskEditorApi } from "api/taskEditorApi";
import {
  normalizeTaskEditorState,
  type PublishedTaskEditorTask,
} from "pages/task-editor/utils/taskEditorRuntime";
import type { TaskEditorState } from "pages/task-editor/utils/taskEditorUtils";
import { useCallback, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import {
  taskEditorDraftsByTaskIdAtom,
  taskEditorPublishedTasksAtom,
  taskEditorStoreHydratedAtom,
  taskEditorStoreLoadingAtom,
  taskEditorWorkingDraftAtom,
} from "recoil/taskEditorStoreAtom";

function sortPublishedTasks(tasks: PublishedTaskEditorTask[]) {
  return [...tasks].sort((left, right) =>
    right.updatedAt.localeCompare(left.updatedAt)
  );
}

function upsertPublishedTask(
  tasks: PublishedTaskEditorTask[],
  nextTask: PublishedTaskEditorTask,
  currentTaskId?: string
) {
  const normalizedCurrentTaskId = currentTaskId?.trim();
  const otherTasks = tasks
    .filter(
      (task) =>
        task.taskId !== nextTask.taskId &&
        task.taskId !== normalizedCurrentTaskId
    )
    .map((task) => {
      if (
        nextTask.isKeyActivity === true &&
        nextTask.setNumber !== undefined &&
        task.setNumber === nextTask.setNumber &&
        task.isKeyActivity === true
      ) {
        return {
          ...task,
          isKeyActivity: false,
        };
      }

      return task;
    });

  return sortPublishedTasks([nextTask, ...otherTasks]);
}

function createFallbackPublishedTask(
  state: TaskEditorState
): PublishedTaskEditorTask {
  const normalizedState = normalizeTaskEditorState(state);
  const now = new Date().toISOString();

  return {
    ...normalizedState,
    taskId: normalizedState.taskId || "NEW-TASK-ID",
    title: normalizedState.title || "새 Task 제목",
    versionId: now,
    updatedAt: now,
  };
}

function getTaskEditorStateSignature(state: TaskEditorState) {
  return JSON.stringify(normalizeTaskEditorState(state));
}

export default function useTaskEditorStore() {
  const [publishedTasks, setPublishedTasks] = useRecoilState(
    taskEditorPublishedTasksAtom
  );
  const [workingDraft, setWorkingDraft] = useRecoilState(
    taskEditorWorkingDraftAtom
  );
  const [taskDraftsByTaskId, setTaskDraftsByTaskId] = useRecoilState(
    taskEditorDraftsByTaskIdAtom
  );
  const [isStoreHydrated, setIsStoreHydrated] = useRecoilState(
    taskEditorStoreHydratedAtom
  );
  const [isStoreLoading, setIsStoreLoading] = useRecoilState(
    taskEditorStoreLoadingAtom
  );
  const latestWorkingDraftRef = useRef<TaskEditorState | undefined>(undefined);
  const latestTaskDraftsByTaskIdRef = useRef<Record<string, TaskEditorState>>(
    {}
  );
  const saveDraftTimerRef = useRef<number | undefined>(undefined);
  const saveTaskDraftTimersRef = useRef<Record<string, number>>({});

  useEffect(() => {
    latestTaskDraftsByTaskIdRef.current = taskDraftsByTaskId;
  }, [taskDraftsByTaskId]);

  useEffect(() => {
    return () => {
      if (saveDraftTimerRef.current !== undefined) {
        window.clearTimeout(saveDraftTimerRef.current);
        saveDraftTimerRef.current = undefined;
      }

      const nextDraft = latestWorkingDraftRef.current;
      if (nextDraft) {
        taskEditorApi.saveWorkingDraft(nextDraft).catch(() => {});
      }

      const taskDraftTimers = saveTaskDraftTimersRef.current;
      Object.entries(taskDraftTimers).forEach(([taskId, timerId]) => {
        window.clearTimeout(timerId);
        delete taskDraftTimers[taskId];

        const nextTaskDraft = latestTaskDraftsByTaskIdRef.current[taskId];
        if (!nextTaskDraft) {
          return;
        }

        taskEditorApi.saveTaskDraft(taskId, nextTaskDraft).catch(() => {});
      });
    };
  }, []);

  const hydratePublishedTasks = useCallback(async () => {
    setIsStoreLoading(true);
    try {
      const nextTasks = await taskEditorApi.getPublishedTasks();
      setPublishedTasks(nextTasks);
      setIsStoreHydrated(true);
      return nextTasks;
    } finally {
      setIsStoreLoading(false);
    }
  }, [setIsStoreHydrated, setIsStoreLoading, setPublishedTasks]);

  const hydrateWorkingDraft = useCallback(async () => {
    const nextDraft: TaskEditorState | undefined =
      await taskEditorApi.getWorkingDraft();
    setWorkingDraft(nextDraft);
    latestWorkingDraftRef.current = nextDraft;
    return nextDraft;
  }, [setWorkingDraft]);

  const hydrateTaskDrafts = useCallback(async () => {
    const nextTaskDraftsByTaskId = await taskEditorApi.getTaskDraftsByTaskId();
    setTaskDraftsByTaskId(nextTaskDraftsByTaskId);
    latestTaskDraftsByTaskIdRef.current = nextTaskDraftsByTaskId;
    return nextTaskDraftsByTaskId;
  }, [setTaskDraftsByTaskId]);

  const hydrateTaskEditorStore = useCallback(async () => {
    setIsStoreLoading(true);
    try {
      const [nextTasks, nextDraft, nextTaskDraftsByTaskId]: [
        PublishedTaskEditorTask[],
        TaskEditorState | undefined,
        Record<string, TaskEditorState>,
      ] = await Promise.all([
        taskEditorApi.getPublishedTasks(),
        taskEditorApi.getWorkingDraft(),
        taskEditorApi.getTaskDraftsByTaskId(),
      ]);
      setPublishedTasks(nextTasks);
      setWorkingDraft(nextDraft);
      setTaskDraftsByTaskId(nextTaskDraftsByTaskId);
      latestWorkingDraftRef.current = nextDraft;
      latestTaskDraftsByTaskIdRef.current = nextTaskDraftsByTaskId;
      setIsStoreHydrated(true);
      return {
        publishedTasks: nextTasks,
        workingDraft: nextDraft,
        taskDraftsByTaskId: nextTaskDraftsByTaskId,
      };
    } finally {
      setIsStoreLoading(false);
    }
  }, [
    setIsStoreHydrated,
    setIsStoreLoading,
    setPublishedTasks,
    setTaskDraftsByTaskId,
    setWorkingDraft,
  ]);

  const saveWorkingDraft = useCallback(
    async (state: TaskEditorState) => {
      const normalizedState = normalizeTaskEditorState(state);
      setWorkingDraft(normalizedState);
      latestWorkingDraftRef.current = normalizedState;

      if (saveDraftTimerRef.current !== undefined) {
        window.clearTimeout(saveDraftTimerRef.current);
      }

      saveDraftTimerRef.current = window.setTimeout(() => {
        const nextDraft = latestWorkingDraftRef.current;
        if (!nextDraft) {
          return;
        }

        taskEditorApi.saveWorkingDraft(nextDraft).catch(() => {});
        saveDraftTimerRef.current = undefined;
      }, 500);
    },
    [setWorkingDraft]
  );

  const publishTask = useCallback(
    async (state: TaskEditorState, currentTaskId?: string) => {
      const normalizedState = normalizeTaskEditorState(state);
      const savedTask =
        (await taskEditorApi.savePublishedTask(
          normalizedState,
          currentTaskId
        )) ?? createFallbackPublishedTask(normalizedState);

      setPublishedTasks((currentTasks) =>
        upsertPublishedTask(currentTasks, savedTask, currentTaskId)
      );

      return savedTask;
    },
    [setPublishedTasks]
  );

  const removeTask = useCallback(
    async (taskId: string) => {
      const normalizedTaskId = taskId.trim();
      await taskEditorApi.removePublishedTask(normalizedTaskId);
      setPublishedTasks((currentTasks) =>
        currentTasks.filter((task) => task.taskId !== normalizedTaskId)
      );
    },
    [setPublishedTasks]
  );

  const saveTaskDraft = useCallback(
    async (taskId: string, state: TaskEditorState) => {
      const normalizedTaskId = taskId.trim();
      if (!normalizedTaskId) {
        return;
      }

      const normalizedState = normalizeTaskEditorState({
        ...state,
        taskId: normalizedTaskId,
      });
      const currentTaskDraft =
        latestTaskDraftsByTaskIdRef.current[normalizedTaskId];
      const nextTaskDraftSignature =
        getTaskEditorStateSignature(normalizedState);

      if (
        currentTaskDraft &&
        getTaskEditorStateSignature(currentTaskDraft) === nextTaskDraftSignature
      ) {
        return;
      }

      setTaskDraftsByTaskId((currentDraftsByTaskId) => {
        const nextDraftsByTaskId = {
          ...currentDraftsByTaskId,
          [normalizedTaskId]: normalizedState,
        };
        latestTaskDraftsByTaskIdRef.current = nextDraftsByTaskId;
        return nextDraftsByTaskId;
      });

      const taskDraftTimers = saveTaskDraftTimersRef.current;
      if (taskDraftTimers[normalizedTaskId] !== undefined) {
        window.clearTimeout(taskDraftTimers[normalizedTaskId]);
      }

      taskDraftTimers[normalizedTaskId] = window.setTimeout(() => {
        const nextTaskDraft =
          latestTaskDraftsByTaskIdRef.current[normalizedTaskId];
        if (!nextTaskDraft) {
          delete taskDraftTimers[normalizedTaskId];
          return;
        }

        taskEditorApi
          .saveTaskDraft(normalizedTaskId, nextTaskDraft)
          .catch(() => {});
        delete taskDraftTimers[normalizedTaskId];
      }, 500);
    },
    [setTaskDraftsByTaskId]
  );

  const removeTaskDraft = useCallback(
    async (taskId: string) => {
      const normalizedTaskId = taskId.trim();
      if (!normalizedTaskId) {
        return;
      }

      const taskDraftTimers = saveTaskDraftTimersRef.current;
      if (taskDraftTimers[normalizedTaskId] !== undefined) {
        window.clearTimeout(taskDraftTimers[normalizedTaskId]);
        delete taskDraftTimers[normalizedTaskId];
      }

      setTaskDraftsByTaskId((currentDraftsByTaskId) => {
        if (!(normalizedTaskId in currentDraftsByTaskId)) {
          return currentDraftsByTaskId;
        }

        const nextDraftsByTaskId = { ...currentDraftsByTaskId };
        delete nextDraftsByTaskId[normalizedTaskId];
        latestTaskDraftsByTaskIdRef.current = nextDraftsByTaskId;
        return nextDraftsByTaskId;
      });

      await taskEditorApi.removeTaskDraft(normalizedTaskId);
    },
    [setTaskDraftsByTaskId]
  );

  return {
    publishedTasks,
    workingDraft,
    taskDraftsByTaskId,
    isStoreHydrated,
    isStoreLoading,
    hydratePublishedTasks,
    hydrateWorkingDraft,
    hydrateTaskDrafts,
    hydrateTaskEditorStore,
    saveWorkingDraft,
    saveTaskDraft,
    publishTask,
    removeTaskDraft,
    removeTask,
  };
}
